import { Host, forceUpdate, h } from "@stencil/core";
import { adoptCommonThemes } from "../../common/theme";
import { EMPTY_LIVE_KIT_ROOM_MESSAGE } from "../live-kit-room/constants";
import { mergeSortedArrays } from "./merge-sort-live-kit-messages";
import { getMessageContent, getMessageFiles } from "./utils";
// Side effect to define the ch-chat-lit custom element
import "./internal/chat.lit";
const ENTER_KEY = "Enter";
const createLiveKitMessagesStore = () => ({
    assistant: new Map(),
    user: new Map()
});
const stopResponseButtonHasAPosition = (sendContainerLayout) => (sendContainerLayout.sendContainerBefore ?? []).includes("stop-response-button") ||
    (sendContainerLayout.sendInputBefore ?? []).includes("stop-response-button") ||
    (sendContainerLayout.sendInputAfter ?? []).includes("stop-response-button") ||
    (sendContainerLayout.sendContainerAfter ?? []).includes("stop-response-button");
const sendContainerLayoutPositionToPartName = {
    sendContainerBefore: "send-container-before",
    sendInputBefore: "send-input-before",
    sendInputAfter: "send-input-after",
    sendContainerAfter: "send-container-after"
};
/**
 * Returns the slot name to be used in the `ch-edit` element.
 */
const getSendInputSlotRename = (slotToRename) => slotToRename === "sendInputBefore"
    ? "additional-content-before"
    : "additional-content-after";
/**
 * TODO: Add description
 */
export class ChChat {
    constructor() {
        this.uploadingFiles = 0;
        this.virtualItems = [];
        this.initialLoadHasEnded = false;
        this.autoScroll = "at-scroll-end";
        this.callbacks = undefined;
        this.disabled = false;
        this.items = [];
        this.liveMode = false;
        this.liveModeConfiguration = undefined;
        this.loadingState = "initial";
        this.markdownTheme = "ch-markdown-viewer";
        this.newUserMessageAlignment = "end";
        this.newUserMessageScrollBehavior = "instant";
        this.renderItem = undefined;
        this.sendButtonDisabled = false;
        this.sendInputDisabled = false;
        this.showAdditionalContent = false;
        this.sendContainerLayout = {
            sendContainerAfter: ["send-button"]
        };
        this.theme = undefined;
        this.translations = {
            accessibleName: {
                clearChat: "Clear chat",
                copyMessageContent: "Copy message content",
                downloadCodeButton: "Download code",
                sendButton: "Send",
                sendInput: "Message",
                stopResponseButton: "Stop generating answer"
            },
            placeholder: {
                sendInput: "Ask me a question..."
            },
            text: {
                copyCodeButton: "Copy code",
                copyMessageContent: "Copy",
                processing: "Processing...",
                sourceFiles: "Source files:"
            }
        };
        this.waitingResponse = false;
    }
    /**
     * Specifies the ID of the cell that is aligned to the start of the scroll
     * position.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #cellIdAlignedWhenRendered;
    /**
     * Specifies a Set of cells that must render an additional div to correctly
     * reserve space. This space is used to align the cell at the start scroll
     * position.
     *
     * Even if only one cell is aligned to the start of the scroll, all cells
     * that participated in this behavior must maintain the additional rendered
     * div to avoid destroying the DOM and thus causing some side effects on
     * elements whose state the chat can not control.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #cellHasToReserveSpace;
    #liveKitTranscriptions; // Allocated at runtime to save resources
    #liveKitMessages; // Allocated at runtime to save resources
    #liveKitCallbacks = {
        activeSpeakersChanged: participant => this.callbacks?.liveMode?.activeSpeakersChanged(participant),
        updateTranscriptions: (segments, participant) => {
            let lastSegmentWithContent;
            for (let index = 0; index < segments.length; index++) {
                const segment = segments[index];
                if (segment.text.trim() !== "" &&
                    segment.text !== EMPTY_LIVE_KIT_ROOM_MESSAGE) {
                    lastSegmentWithContent = segment;
                }
            }
            // Don't add empty messages
            if (lastSegmentWithContent === undefined) {
                return;
            }
            const messageRole = participant.isLocal ? "user" : "assistant";
            this.#liveKitTranscriptions[messageRole].set(lastSegmentWithContent.id, lastSegmentWithContent);
            this.#liveKitMessages = mergeSortedArrays(this.#liveKitTranscriptions);
            forceUpdate(this);
        }
    };
    #mustReplaceSendButtonWithStopResponse = true;
    // Refs
    #chatLitRef;
    #editRef;
    #smartGridRef;
    #virtualScrollRef;
    el;
    itemsChanged() {
        this.#cellIdAlignedWhenRendered = undefined;
        this.initialLoadHasEnded = false;
        // Free the memory, since no cells will have reserved space as the model
        // is different
        this.#cellHasToReserveSpace = undefined;
        // TODO: This is a WA to remove the anchor cell when the model changes. We
        // should find a better way to remove the reserved space for the anchor
        // cell
        this.#smartGridRef?.removeScrollEndContentReference();
    }
    liveModeChanged() {
        if (this.liveMode) {
            this.#liveKitTranscriptions = createLiveKitMessagesStore();
            this.#liveKitMessages = [];
        }
        else {
            this.#virtualScrollRef?.addItems("end", ...this.#liveKitMessages);
            this.#liveKitTranscriptions = undefined;
            // Wait for the virtual scroller to emit the new message, in order to
            // reduce flickering
            requestAnimationFrame(() => {
                this.#liveKitMessages = undefined;
            });
        }
    }
    newUserMessageAlignmentChanged() {
        if (this.newUserMessageAlignment === "end") {
            this.#cellIdAlignedWhenRendered = undefined;
            // Don't reset the `cellHasToReserveSpace` Set here, because the render
            // of the items that belongs to the Set will be destroyed and re-created
            // to only remove one div
        }
    }
    sendContainerLayoutChanged() {
        this.#mustReplaceSendButtonWithStopResponse =
            !stopResponseButtonHasAPosition(this.sendContainerLayout);
    }
    /**
     * Fired when a new user message is added in the chat via user interaction.
     *
     * Since developers can define their own render for file attachment, this
     * event serves to synchronize the cleanup of the `send-input` with the
     * cleanup of the custom file attachment, or or even blocking user
     * interactions before the `sendChatMessages` callback is executed.
     */
    userMessageAdded;
    /**
     * Add a new message at the end of the record, performing a re-render.
     */
    async addNewMessage(message) {
        this.initialLoadHasEnded = true;
        if (this.newUserMessageAlignment === "start") {
            this.#cellHasToReserveSpace ??= new Set();
            this.#cellHasToReserveSpace.add(message.id);
        }
        this.#pushMessage(message);
    }
    /**
     * Focus the chat input
     */
    async focusChatInput() {
        this.#editRef?.focus();
    }
    /**
     * Set the text for the chat input
     */
    async setChatInputMessage(text) {
        if (this.#editRef) {
            this.#editRef.value = text;
        }
    }
    /**
     * Send the current message of the ch-chat's `send-input` element. This
     * method executes the same callbacks and interoperates with the same
     * features as if the message were sent through user interaction. The only
     * things to keep in mind are the following:
     *  - If the `content` parameter is provided, it will be used in replacement
     *    of the input content.
     *
     *  - If the `files` parameter is provided, the `getChatMessageFiles`
     *    callback won't be executed to get the current files of the chat.
     *
     * Whether or not the `content` parameter is provided, the content of the
     * `send-input` element will be cleared.
     */
    async sendChatMessage(content, files) {
        return this.#sendMessage(content, files);
    }
    /**
     * Given the id of the message, it updates the content of the indexed message.
     */
    async updateChatMessage(messageIndex, message, mode) {
        // TODO: Don't update chat message if it has role="user" and it is
        // uploading file messages
        if (this.items.length === 0 || !this.items[messageIndex]) {
            return;
        }
        this.initialLoadHasEnded = true;
        this.#updateMessage(messageIndex, message, mode);
        const updatedMessage = this.items[messageIndex];
        const virtualItemIndex = this.virtualItems.findIndex(virtualItem => virtualItem.id === updatedMessage.id);
        // Sync the last virtual item with the real item that is updated
        if (virtualItemIndex !== -1) {
            this.virtualItems[virtualItemIndex] = updatedMessage;
        }
        forceUpdate(this);
    }
    // TODO: Add unit tests to validate how the chat message should be copied
    // into the last chat message, considering that messages can have more
    // properties that the interface/type has
    /**
     * Update the content of the last message, performing a re-render.
     */
    async updateLastMessage(message, mode) {
        // TODO: Don't update last chat message if it has role="user" and it is
        // uploading file messages
        if (this.items.length === 0) {
            return;
        }
        this.initialLoadHasEnded = true;
        this.#updateMessage(this.items.length - 1, message, mode);
        // Sync the last virtual item with the real item that is updated
        this.virtualItems[this.virtualItems.length - 1] = this.items.at(-1);
        forceUpdate(this);
    }
    #pushMessage = async (message) => {
        // We should also check for the virtual-scroller to be defined, because the
        // user can add two messages at once, without waiting for the
        // virtual-scroller to be defined
        if (this.items.length === 0 || !this.#virtualScrollRef) {
            this.items.push(message);
            forceUpdate(this);
        }
        else {
            await this.#virtualScrollRef.addItems("end", message);
        }
    };
    #updateMessage = (messageIndex, message, mode) => {
        if (mode === "concat") {
            const messageInIndex = this.items[messageIndex];
            // Temporal store for the new message content
            const newMessageContent = getMessageContent(messageInIndex) + getMessageContent(message);
            // Temporal store for the new message files
            const newMessageFiles = getMessageFiles(messageInIndex);
            newMessageFiles.push(...getMessageFiles(message));
            // Update the message object, since the current message could not be an
            // object with files
            if (newMessageFiles.length !== 0) {
                // Reuse the message parameter reference to correctly update the
                // message content. We are doing this since it can contain new member
                // the message that are not present in the current message
                message.content = {
                    message: newMessageContent,
                    files: newMessageFiles
                };
            }
            else if (typeof message.content === "string") {
                message.content = newMessageContent;
            }
            else {
                message.content.message = newMessageContent;
            }
        }
        // Replace the message
        const messageId = this.items[messageIndex].id;
        this.items[messageIndex] = Object.assign({ id: messageId }, message);
    };
    // TODO: Sending the chat by pressing the Enter key should be a property
    #sendMessageKeyboard = (event) => {
        if (event.key !== ENTER_KEY || event.shiftKey) {
            return;
        }
        event.preventDefault();
        this.#sendMessage();
    };
    #addUserMessageToRecordAndFocusInput = async (userMessage) => {
        this.#editRef.value = "";
        this.#editRef.click(); // TODO: Should it be focus???
        await this.#pushMessage(userMessage);
        this.userMessageAdded.emit(userMessage);
    };
    #chatMessageCanBeSent = (chat, files) => !this.callbacks ||
        !this.callbacks.validateSendChatMessage ||
        this.callbacks.validateSendChatMessage(chat, files);
    #getChatFiles = () => !this.callbacks || !this.callbacks.getChatMessageFiles
        ? []
        : this.callbacks.getChatMessageFiles();
    #liveModeIsDisplayed = () => {
        const config = this.liveModeConfiguration;
        return this.liveMode && config && !!config.url && !!config.token;
    };
    #renderLiveKitRoom = () => {
        const config = this.liveModeConfiguration;
        return (this.#liveModeIsDisplayed() && (h("ch-live-kit-room", { callbacks: this.#liveKitCallbacks, connected: true, microphoneEnabled: config.localParticipant?.microphoneEnabled ?? true, token: config.token, url: config.url })));
    };
    #uploadFiles = (userMessageToAdd, sendInputValue, filesToUpload) => {
        const callbacks = this.callbacks;
        if (!callbacks) {
            // eslint-disable-next-line no-console
            return console.warn('The "callbacks" property is not defined, so files can not be uploaded');
        }
        if (!callbacks.uploadFile) {
            // eslint-disable-next-line no-console
            return console.warn('The "uploadFile" member is not defined in the "callbacks" property, so files can not be uploaded');
        }
        const chatFiles = [];
        userMessageToAdd.content = {
            message: sendInputValue,
            files: chatFiles
        };
        // Add all files as "in-progress" state and update them to the server in
        // parallel
        for (let fileIndex = 0; fileIndex < filesToUpload.length; fileIndex++) {
            const file = filesToUpload[fileIndex];
            const temporalFileURL = URL.createObjectURL(file);
            chatFiles.push({
                caption: file.name,
                mimeType: file.type,
                uploadState: "in-progress",
                url: temporalFileURL
            });
            this.uploadingFiles++;
            callbacks
                .uploadFile(file)
                .then(uploadedFile => {
                uploadedFile.uploadState = "uploaded";
                chatFiles[fileIndex] = uploadedFile;
            })
                .catch(() => {
                chatFiles[fileIndex].uploadState = "failed";
            })
                .finally(async () => {
                this.uploadingFiles--;
                // TODO: If the file upload fails, the preview will be removed
                // without replacing it with the actual public URL
                // Free the resource to avoid memory leaks
                URL.revokeObjectURL(temporalFileURL);
                if (this.uploadingFiles === 0) {
                    this.#sendChat();
                }
            });
        }
    };
    #sendChat = () => {
        const lastCell = this.items.at(-1);
        this.#cellIdAlignedWhenRendered = lastCell.id;
        if (this.newUserMessageAlignment === "start") {
            this.initialLoadHasEnded = true;
            this.#cellHasToReserveSpace ??= new Set();
            this.#cellHasToReserveSpace.add(lastCell.id);
        }
        if (!this.callbacks) {
            // eslint-disable-next-line no-console
            return console.warn('The "callbacks" property is not defined, so the "sendChatMessages" function can not be executed to emit the new chat');
        }
        this.callbacks.sendChatMessages(this.items);
    };
    #sendMessage = async (content, files) => {
        // TODO: Add unit tests for this
        if (this.waitingResponse ||
            this.disabled ||
            this.liveMode ||
            this.loadingState === "initial" ||
            this.loadingState === "loading" ||
            this.uploadingFiles !== 0) {
            return;
        }
        const filesToUpload = files ?? (await this.#getChatFiles());
        const sendInputValue = content
            ? getMessageContent(content)
            : this.#editRef.value;
        const hasFiles = filesToUpload.length !== 0;
        const emptySendInput = (!sendInputValue || sendInputValue.trim() === "") && !hasFiles;
        if (emptySendInput) {
            return;
        }
        // Message
        const userMessageToAdd = content ?? {
            id: `${new Date().getTime()}`,
            role: "user",
            content: sendInputValue
        };
        // Validate message with callback
        if (!(await this.#chatMessageCanBeSent(userMessageToAdd, filesToUpload))) {
            return;
        }
        // Upload files to the server as soon as possible
        if (hasFiles) {
            this.#uploadFiles(userMessageToAdd, sendInputValue, filesToUpload);
        }
        // TODO: Should we do something if the uploadFile callback is not defined??
        await this.#addUserMessageToRecordAndFocusInput(userMessageToAdd);
        // Only send the chat if the user message didn't have files. Otherwise, the
        // chat sending will be resolved in the uploadFiles
        if (!hasFiles) {
            this.#sendChat();
        }
        // Queue a new re-render
        forceUpdate(this);
    };
    #sendMessageWithSendButton = () => this.#sendMessage();
    #stopResponse = (event) => {
        event.stopPropagation();
        this.callbacks.stopResponse();
    };
    #virtualItemsChanged = (event) => {
        this.virtualItems = event.detail.virtualItems;
    };
    // - - - - - - - - - - - - - - - - - - - -
    //                 Renders
    // - - - - - - - - - - - - - - - - - - - -
    #renderChatOrEmpty = () => this.loadingState === "all-records-loaded" && this.items.length === 0 ? (h("slot", { name: "empty-chat" })) : (h("ch-smart-grid", { autoScroll: 
        // We have to bind the property this way to make sure the scroll is
        // positioned correctly at the initial load. Otherwise, if really
        // hard to position the scroll if we don't know somehow when the
        // initial load has finished
        this.initialLoadHasEnded ? this.autoScroll : "at-scroll-end", dataProvider: this.loadingState === "more-data-to-fetch", loadingState: this.virtualItems.length === 0 ? "initial" : this.loadingState, inverseLoading: true, itemsCount: this.virtualItems.length, onInfiniteThresholdReached: this.#loadMoreItems, ref: el => (this.#smartGridRef = el) }, h("ch-virtual-scroller", { role: "row", slot: "grid-content", class: "grid-content", part: "messages-container", inverseLoading: true,
        // mode="lazy-render"
        items: this.items, itemsCount: this.items.length, onVirtualItemsChanged: this.#virtualItemsChanged, ref: el => (this.#virtualScrollRef = el) }, h("ch-chat-lit", { cellHasToReserveSpace: this.#cellHasToReserveSpace, cellIdAlignedWhenRendered: this.#cellIdAlignedWhenRendered, chatRef: this.el, newUserMessageAlignment: this.newUserMessageAlignment, newUserMessageScrollBehavior: this.newUserMessageScrollBehavior, renderItem: this.renderItem, smartGridRef: this.#smartGridRef, virtualItems: this.virtualItems, ref: el => (this.#chatLitRef = el) }))));
    #renderStopResponseButton = () => {
        const { accessibleName, text } = this.translations;
        const ariaLabel = accessibleName.stopResponseButton !== text.stopResponseButton
            ? accessibleName.stopResponseButton ?? text.stopResponseButton
            : undefined;
        return (this.waitingResponse &&
            this.callbacks?.stopResponse && (h("button", { "aria-label": ariaLabel, title: ariaLabel, part: "stop-response-button", type: "button", onClick: this.#stopResponse }, text.stopResponseButton)));
    };
    #renderSendOrStopResponseButton = () => this.waitingResponse &&
        this.callbacks?.stopResponse &&
        this.#mustReplaceSendButtonWithStopResponse
        ? this.#renderStopResponseButton()
        : this.#renderSendButton();
    #renderSendButton = () => {
        const { accessibleName } = this.translations;
        const sendButtonDisabled = this.sendButtonDisabled ||
            this.disabled ||
            this.loadingState === "initial" ||
            this.#liveModeIsDisplayed();
        return (h("button", { "aria-label": accessibleName.sendButton, title: accessibleName.sendButton, part: "send-button", disabled: sendButtonDisabled, type: "button", onClick: sendButtonDisabled ? undefined : this.#sendMessageWithSendButton }));
    };
    #renderSendContainerLayoutElement = (elementName) => {
        if (elementName === "send-button") {
            return this.#renderSendOrStopResponseButton();
        }
        return elementName === "stop-response-button" ? (this.#renderStopResponseButton()) : (h("slot", { name: elementName }));
    };
    #renderSendContainerOrSendInputSlots = (containerName, containerElements) => {
        // No elements in the container position
        if (containerElements === undefined || containerElements.length === 0) {
            return undefined;
        }
        // The container only had the stop-response-button, but it is not rendered,
        // so we must avoid rendering the container with empty content
        if (containerElements.length === 1 &&
            containerElements[0] === "stop-response-button" &&
            (!this.waitingResponse || this.callbacks?.stopResponse === undefined)) {
            return undefined;
        }
        const partName = sendContainerLayoutPositionToPartName[containerName];
        return (h("div", {
            // Project the send-input slots if necessary
            slot: containerName === "sendInputAfter" ||
                containerName === "sendInputBefore"
                ? getSendInputSlotRename(containerName)
                : undefined, class: `additional-content-container ${partName}`, part: partName
        }, containerElements.map(this.#renderSendContainerLayoutElement)));
    };
    #loadMoreItems = () => {
        this.loadingState = "loading";
        // WA to test
        setTimeout(() => {
            const totalItems = this.items.length;
            const newItems = Array.from({ length: 20 }, (_, index) => index % 2 === 0
                ? {
                    id: `index: ${index - totalItems}`,
                    role: "user",
                    content: `index: ${index - totalItems}` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n`
                }
                : {
                    id: `index: ${index - totalItems}`,
                    role: "assistant",
                    content: `\nindex: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n` +
                        `index: ${index - totalItems}\n`
                });
            this.#virtualScrollRef.addItems("start", ...newItems);
            this.loadingState = "more-data-to-fetch";
        }, 10);
    };
    connectedCallback() {
        // Scrollbar styles
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        if (this.liveMode) {
            this.#liveKitTranscriptions = createLiveKitMessagesStore();
            this.#liveKitMessages = [];
        }
        this.#mustReplaceSendButtonWithStopResponse =
            !stopResponseButtonHasAPosition(this.sendContainerLayout);
    }
    componentDidUpdate() {
        // At this moment, for safety we should always re-render the ch-chat-lit
        // when the ch-chat is re-rendered
        this.#chatLitRef?.requestUpdate();
    }
    render() {
        const { sendContainerBefore, sendInputAfter, sendInputBefore, sendContainerAfter } = this.sendContainerLayout;
        const canShowAdditionalContent = this.showAdditionalContent &&
            // It's not the initial load
            this.loadingState !== "initial" &&
            // It's not the empty chat
            !(this.items.length === 0 && this.loadingState === "all-records-loaded");
        const liveModeIsDisplayed = this.#liveModeIsDisplayed();
        const sendInputDisabled = this.sendInputDisabled || this.disabled || liveModeIsDisplayed;
        return (h(Host, { key: '3fa7c0ce4f02d87690361533653f4fbb64098697', class: canShowAdditionalContent ? "ch-chat--additional-content" : undefined }, this.theme && h("ch-theme", { key: 'f548c24b72958b0838ba7327d3f184153dfb1aca', model: this.theme }), this.loadingState === "initial" ? (h("slot", { name: "loading-chat" })) : (this.#renderChatOrEmpty()), canShowAdditionalContent && h("slot", { key: '5e5ddfce5b5d0ab85d0faa9194d20676d0e22d4d', name: "additional-content" }), h("div", { key: '0f4f7d81bc2e34ef729ce0766f80b65b8ac0e03c', class: "send-container", part: "send-container" }, this.#renderSendContainerOrSendInputSlots("sendContainerBefore", sendContainerBefore), h("ch-edit", { key: '8a9adfb7ecc1b8c20bf6b29d20fe98680554ba9b', class: "send-input", accessibleName: this.translations.accessibleName.sendInput, autoGrow: true, disabled: sendInputDisabled, hostParts: "send-input", multiline: true, placeholder: this.translations.placeholder.sendInput, preventEnterInInputEditorMode: true, showAdditionalContentAfter: sendInputAfter !== undefined && sendInputAfter.length !== 0, showAdditionalContentBefore: sendInputBefore !== undefined && sendInputBefore.length !== 0, onKeyDown: sendInputDisabled || this.liveMode
                ? undefined
                : this.#sendMessageKeyboard, ref: el => (this.#editRef = el) }, this.#renderSendContainerOrSendInputSlots("sendInputBefore", sendInputBefore), this.#renderSendContainerOrSendInputSlots("sendInputAfter", sendInputAfter)), this.#renderSendContainerOrSendInputSlots("sendContainerAfter", sendContainerAfter)), this.#renderLiveKitRoom()));
    }
    static get is() { return "ch-chat"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["chat.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["chat.css"]
        };
    }
    static get properties() {
        return {
            "autoScroll": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"never\" | \"at-scroll-end\"",
                    "resolved": "\"at-scroll-end\" | \"never\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the scroll position will be adjusted when the chat messages\nare updated with the methods `addNewMessage`, `updateChatMessage` or\n`updateLastMessage`.\n  - \"at-scroll-end\": If the scroll is positioned at the end of the content,\n  the chat will maintain the scroll at the end while the content of the\n  messages is being updated.\n\n - \"never\": The scroll position won't be adjusted when the content of the\n  messages is being updated."
                },
                "attribute": "auto-scroll",
                "reflect": false,
                "defaultValue": "\"at-scroll-end\""
            },
            "callbacks": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ChatCallbacks | undefined",
                    "resolved": "{ downloadCodeBlock?: (plainText: string, language: string) => void; getChatMessageFiles?: () => File[] | Promise<File[]>; liveMode?: Pick<LiveKitCallbacks, \"activeSpeakersChanged\">; sendChatMessages: (chat: ChatMessage[]) => void; stopResponse?: () => Promise<void>; validateSendChatMessage?: (chat: ChatMessage, files: File[]) => boolean | Promise<boolean>; uploadFile?: (file: File) => Promise<ChatMessageFile>; }",
                    "references": {
                        "ChatCallbacks": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatCallbacks"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the callbacks required in the control."
                }
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if all interactions are disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "items": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "ChatMessage[]",
                    "resolved": "ChatMessage[]",
                    "references": {
                        "ChatMessage": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessage"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items that the chat will display."
                },
                "defaultValue": "[]"
            },
            "liveMode": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the live mode is set.\n\nWhen this mode is enabled, the chat will disable sending messages by user\ninteractions and the only way to send messages will be throughout the\nvoice. The user will have to enable the microphone input in their Operative\nSystem and it will voice chat with the remote participants.\n\nWhen any participant speaks, the transcribed conversation will be displayed\nas new messages in the chat (`items` property).\n\nWhen the `liveMode` ends, the transcribed conversation will be pushed\nto the `items` of the chat."
                },
                "attribute": "live-mode",
                "reflect": false,
                "defaultValue": "false"
            },
            "liveModeConfiguration": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ChatLiveModeConfiguration | undefined",
                    "resolved": "{ url: string; token: string; localParticipant?: { microphoneEnabled?: boolean; }; }",
                    "references": {
                        "ChatLiveModeConfiguration": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatLiveModeConfiguration"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the live mode configuration. The `token` and `url` are required\nto enable the `liveMode`."
                }
            },
            "loadingState": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "SmartGridDataState",
                    "resolved": "\"all-records-loaded\" | \"initial\" | \"loading\" | \"more-data-to-fetch\"",
                    "references": {
                        "SmartGridDataState": {
                            "location": "import",
                            "path": "../smart-grid/internal/infinite-scroll/types",
                            "id": "src/components/smart-grid/internal/infinite-scroll/types.ts::SmartGridDataState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the chat is waiting for the data to be loaded."
                },
                "attribute": "loading-state",
                "reflect": false,
                "defaultValue": "\"initial\""
            },
            "markdownTheme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | null",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the theme to be used for rendering the markdown.\nIf `null`, no theme will be applied."
                },
                "attribute": "markdown-theme",
                "reflect": false,
                "defaultValue": "\"ch-markdown-viewer\""
            },
            "newUserMessageAlignment": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"start\" | \"end\"",
                    "resolved": "\"end\" | \"start\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the messages added by the user interaction will be aligned\nin the chat.\n\nIf `newUserMessageAlignment === \"start\"` the chat will reserve the\nnecessary space to visualize the message at the start of the content\nviewport if the content is not large enough.\nThis behavior is the same as the Monaco editor does for reserving space\nwhen visualizing the last lines positioned at the top of the editor."
                },
                "attribute": "new-user-message-alignment",
                "reflect": false,
                "defaultValue": "\"end\""
            },
            "newUserMessageScrollBehavior": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<\n    ScrollBehavior,\n    \"auto\"\n  >",
                    "resolved": "\"instant\" | \"smooth\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ScrollBehavior": {
                            "location": "global",
                            "id": "global::ScrollBehavior"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the chat will scroll to the position of the messages added\nby user interaction."
                },
                "attribute": "new-user-message-scroll-behavior",
                "reflect": false,
                "defaultValue": "\"instant\""
            },
            "renderItem": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "| ChatMessageRenderByItem\n    | ChatMessageRenderBySections\n    | undefined",
                    "resolved": "((messageModel: ChatMessageError | ChatMessageUser | ChatMessageAssistant) => string | TemplateResult) | { actions?: ChatActionsRender; codeBlock?: ChatCodeBlockRender; contentBefore?: ChatContentRender; content?: ChatContentRender; contentAfter?: ChatContentRender; file?: ChatFileRender; messageStructure?: ChatMessageStructureRender; source?: ChatSourceRender; }",
                    "references": {
                        "ChatMessageRenderByItem": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessageRenderByItem"
                        },
                        "ChatMessageRenderBySections": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessageRenderBySections"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property allows us to implement custom rendering of chat items.\n\nThis works by providing a custom render of the cell content in two\npossible ways:\n  1. Replacing the render of the entire cell with a function of the\n  message model.\n\n  2. Replacing the render of specific parts of the message by providing an\n  object with the specific renders of the message sections (`codeBlock`,\n  `contentBefore`, `content`, `contentAfter`, `files` and/or\n  `messageStructure`)."
                }
            },
            "sendButtonDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to disable the send-button element."
                },
                "attribute": "send-button-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "sendInputDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to disable the send-input element."
                },
                "attribute": "send-input-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "showAdditionalContent": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render a slot named \"additional-content\" to project elements\nbetween the \"content\" slot (grid messages) and the \"send-container\" slot.\n\nThis slot can only be rendered if loadingState !== \"initial\" and\n(loadingState !== \"all-records-loaded\" && items.length > 0)."
                },
                "attribute": "show-additional-content",
                "reflect": false,
                "defaultValue": "false"
            },
            "sendContainerLayout": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ChatSendContainerLayout",
                    "resolved": "{ sendContainerBefore?: ChatSendContainerLayoutElement[]; sendInputBefore?: ChatSendContainerLayoutElement[]; sendInputAfter?: ChatSendContainerLayoutElement[]; sendContainerAfter?: ChatSendContainerLayoutElement[]; }",
                    "references": {
                        "ChatSendContainerLayout": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatSendContainerLayout"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "example",
                            "text": "If you define:\n```ts\nconst sendContainerLayout: ChatSendContainerLayout = {\n  sendInputBefore: [\"attach-files-button\"],\n  sendInputAfter: [\"send-button\"]\n}\n```\n\nYou will have to put `slot=\"attach-files-button\"` as follows (but not the\n`send-button` as this is a reserved element that is rendered internally by\nthe `ch-chat`):\n```tsx\n<ch-chat\n  sendContainerLayout={sendContainerLayout}\n>\n  <button slot=\"attach-files-button\" type=\"button\">\n    ...\n  </button>\n</ch-chat>\n```"
                        }],
                    "text": "Specifies the position of the elements in the `send-container` part.\nThere are four positions for distributing elements:\n  - `sendContainerBefore`: Before the contents of the `send-container` part.\n  - `sendInputBefore`: Before the contents of the `send-input` part.\n  - `sendInputAfter`: After the contents of the `send-input` part.\n  - `sendContainerAfter`: After the contents of the `send-container` part.\n\nAt each position you can specify reserved elements, such as the\n`send-button` and `stop-response-button`, but can also be specified\nnon-reserved elements, which will be projected as content slots.\n\nIf the reserved `stop-response-button` element is not specified anywhere,\nthe send button will be replaced with the stop-response button\nwhen `waitingResponse = true` and the `stopResponse` callback is specified.\n\nIf the `send-button` is not specified in any position, it won't be\nrendered in the `ch-chat`."
                },
                "defaultValue": "{\n    sendContainerAfter: [\"send-button\"]\n  }"
            },
            "theme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ThemeModel | undefined",
                    "resolved": "ThemeItemBaseModel & { styleSheet: string; } | ThemeItemBaseModel & { url?: string; } | ThemeItemModel[] | string | string[]",
                    "references": {
                        "ThemeModel": {
                            "location": "import",
                            "path": "../theme/theme-types",
                            "id": "src/components/theme/theme-types.ts::ThemeModel"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the theme to be used for rendering the chat.\nIf `undefined`, no theme will be applied."
                },
                "attribute": "theme",
                "reflect": false
            },
            "translations": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ChatTranslations",
                    "resolved": "{ accessibleName: { clearChat: string; copyMessageContent?: string; downloadCodeButton?: string; sendButton: string; sendInput: string; stopResponseButton?: string; }; placeholder: { sendInput: string; }; text: { copyCodeButton: string; copyMessageContent?: string; downloadCodeButton?: string; processing?: string; sourceFiles?: string; stopResponseButton?: string; }; }",
                    "references": {
                        "ChatTranslations": {
                            "location": "import",
                            "path": "./translations",
                            "id": "src/components/chat/translations.ts::ChatTranslations"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the literals required in the control."
                },
                "defaultValue": "{\n    accessibleName: {\n      clearChat: \"Clear chat\",\n      copyMessageContent: \"Copy message content\",\n      downloadCodeButton: \"Download code\",\n      sendButton: \"Send\",\n      sendInput: \"Message\",\n      stopResponseButton: \"Stop generating answer\"\n    },\n    placeholder: {\n      sendInput: \"Ask me a question...\"\n    },\n    text: {\n      copyCodeButton: \"Copy code\",\n      copyMessageContent: \"Copy\",\n      processing: \"Processing...\",\n      sourceFiles: \"Source files:\"\n    }\n  }"
            },
            "waitingResponse": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "`true` if the `ch-chat` is waiting for a response from the server. If so,\nthe `sendChatMessages` won't be executed when the user tries to send a new\nmessage. Although, the `send-input` and `send-button` won't be disabled,\nso the user can interact with the chat."
                },
                "attribute": "waiting-response",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "uploadingFiles": {},
            "virtualItems": {},
            "initialLoadHasEnded": {}
        };
    }
    static get events() {
        return [{
                "method": "userMessageAdded",
                "name": "userMessageAdded",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a new user message is added in the chat via user interaction.\n\nSince developers can define their own render for file attachment, this\nevent serves to synchronize the cleanup of the `send-input` with the\ncleanup of the custom file attachment, or or even blocking user\ninteractions before the `sendChatMessages` callback is executed."
                },
                "complexType": {
                    "original": "ChatMessageByRole<\"user\">",
                    "resolved": "{ id: string; role: \"user\"; content: ChatMessageContent; metadata?: any; parts?: string; transcribed?: boolean; }",
                    "references": {
                        "ChatMessageByRole": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessageByRole"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "addNewMessage": {
                "complexType": {
                    "signature": "(message: ChatMessage) => Promise<void>",
                    "parameters": [{
                            "name": "message",
                            "type": "ChatMessageError | ChatMessageSystem | ChatMessageUser | ChatMessageAssistant",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ChatMessage": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessage"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Add a new message at the end of the record, performing a re-render.",
                    "tags": []
                }
            },
            "focusChatInput": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the chat input",
                    "tags": []
                }
            },
            "setChatInputMessage": {
                "complexType": {
                    "signature": "(text: string) => Promise<void>",
                    "parameters": [{
                            "name": "text",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set the text for the chat input",
                    "tags": []
                }
            },
            "sendChatMessage": {
                "complexType": {
                    "signature": "(content?: ChatMessageUser | undefined, files?: File[]) => Promise<void>",
                    "parameters": [{
                            "name": "content",
                            "type": "{ id: string; role: \"user\"; content: ChatMessageContent; metadata?: any; parts?: string; transcribed?: boolean; }",
                            "docs": ""
                        }, {
                            "name": "files",
                            "type": "File[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ChatMessageUser": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessageUser"
                        },
                        "File": {
                            "location": "global",
                            "id": "global::File"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Send the current message of the ch-chat's `send-input` element. This\nmethod executes the same callbacks and interoperates with the same\nfeatures as if the message were sent through user interaction. The only\nthings to keep in mind are the following:\n - If the `content` parameter is provided, it will be used in replacement\n   of the input content.\n\n - If the `files` parameter is provided, the `getChatMessageFiles`\n   callback won't be executed to get the current files of the chat.\n\nWhether or not the `content` parameter is provided, the content of the\n`send-input` element will be cleared.",
                    "tags": []
                }
            },
            "updateChatMessage": {
                "complexType": {
                    "signature": "(messageIndex: number, message: ChatMessageByRoleNoId<\"system\" | \"assistant\">, mode: \"concat\" | \"replace\") => Promise<void>",
                    "parameters": [{
                            "name": "messageIndex",
                            "type": "number",
                            "docs": ""
                        }, {
                            "name": "message",
                            "type": "Omit<ChatMessageSystem, \"id\"> | Omit<ChatMessageAssistant, \"id\">",
                            "docs": ""
                        }, {
                            "name": "mode",
                            "type": "\"concat\" | \"replace\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ChatMessageByRoleNoId": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessageByRoleNoId"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given the id of the message, it updates the content of the indexed message.",
                    "tags": []
                }
            },
            "updateLastMessage": {
                "complexType": {
                    "signature": "(message: ChatMessageByRoleNoId<\"system\" | \"assistant\">, mode: \"concat\" | \"replace\") => Promise<void>",
                    "parameters": [{
                            "name": "message",
                            "type": "Omit<ChatMessageSystem, \"id\"> | Omit<ChatMessageAssistant, \"id\">",
                            "docs": ""
                        }, {
                            "name": "mode",
                            "type": "\"concat\" | \"replace\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ChatMessageByRoleNoId": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/chat/types.ts::ChatMessageByRoleNoId"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the content of the last message, performing a re-render.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "items",
                "methodName": "itemsChanged"
            }, {
                "propName": "liveMode",
                "methodName": "liveModeChanged"
            }, {
                "propName": "newUserMessageAlignment",
                "methodName": "newUserMessageAlignmentChanged"
            }, {
                "propName": "sendContainerLayout",
                "methodName": "sendContainerLayoutChanged"
            }];
    }
}
